import * as joint from '@joint/core';
import { applyConnectorStyleMixin } from './constants';
import { FlowType } from './types';

export class PhysicalLink extends joint.shapes.standard.Link {
    defaults() {
        return joint.util.defaultsDeep({
            type: 'PhysicalLink',
            color: '#CE1210', // custom attribute - metadata only
            attrs: {
                line: {
                    stroke: '#CE1210',
                    strokeWidth: 3,
                    targetMarker: {
                        d: 'M 10 -5 0 0 10 5 Z',
                    }
                },
            },
        }, joint.shapes.standard.Link.prototype.defaults);
    }
}

export class LegalLink extends joint.shapes.standard.Link {
    defaults() {
        return joint.util.defaultsDeep({
            type: 'LegalLink',
            color: '#99C13C', // custom attribute - metadata only
            dashed: true, // custom attribute - metadata only
            attrs: {
                line: {
                    stroke: '#99C13C',
                    strokeWidth: 3,
                    strokeDasharray: '6, 5',
                    targetMarker: {
                        d: 'M 10 -5 0 0 10 5 Z',
                    }
                },
            },
        }, joint.shapes.standard.Link.prototype.defaults);
    }
}

export class PaymentLink extends joint.shapes.standard.Link {
    defaults() {
        return joint.util.defaultsDeep({
            type: 'PaymentLink',
            color: '#9B9B9D', // custom attribute - metadata only
            dashed: true, // custom attribute - metadata only
            attrs: {
                line: {
                    stroke: '#9B9B9D',
                    strokeWidth: 3,
                    strokeDasharray: '6, 5',
                    targetMarker: {
                        d: 'M 10 -5 0 0 10 5 Z',
                    }
                },
            },
        }, joint.shapes.standard.Link.prototype.defaults);
    }
}

class ServiceLink extends joint.shapes.standard.Link {
    defaults() {
        return joint.util.defaultsDeep({
            type: 'ServiceLink',
            color: '#000000', // custom attribute - metadata only
            attrs: {
                line: {
                    stroke: '#000000',
                    strokeWidth: 3,
                    targetMarker: {
                        d: 'M 10 -5 0 0 10 5 Z',
                    }
                },
            },
        }, joint.shapes.standard.Link.prototype.defaults);
    }
}

class IPTransferLink extends joint.shapes.standard.Link {
    defaults() {
        return joint.util.defaultsDeep({
            type: 'IPTransferLink',
            color: '#FFA500', // custom attribute - metadata only
            attrs: {
                line: {
                    stroke: '#FFA500',
                    strokeWidth: 3,
                    targetMarker: {
                        d: 'M 10 -5 0 0 10 5 Z',
                    }
                },
            },
        }, joint.shapes.standard.Link.prototype.defaults);
    }
}

class IPLicenseLink extends joint.shapes.standard.Link {
    defaults() {
        return joint.util.defaultsDeep({
            type: 'IPLicenseLink',
            color: '#8E25AB', // custom attribute - metadata only
            attrs: {
                line: {
                    stroke: '#8E25AB',
                    strokeWidth: 3,
                    targetMarker: {
                        d: 'M 10 -5 0 0 10 5 Z',
                    }
                },
            },
        }, joint.shapes.standard.Link.prototype.defaults);
    }
}

class AgreementLink extends joint.shapes.standard.Link {
    defaults() {
        return joint.util.defaultsDeep({
            type: 'AgreementLink',
            color: '#256F8C', // custom attribute - metadata only
            attrs: {
                line: {
                    stroke: '#256F8C',
                    strokeWidth: 3,
                    targetMarker: {
                        d: 'M 10 -5 0 0 10 5 Z',
                    },
                    sourceMarker: {
                        d: 'M 10 -5 0 0 10 5 Z',
                    }
                },
            },
        }, joint.shapes.standard.Link.prototype.defaults);
    }
}

class MiscellaneousLink extends joint.shapes.standard.Link {
    defaults() {
        return joint.util.defaultsDeep({
            type: 'MiscellaneousLink',
            color: '#EB8100', // custom attribute - metadata only
            dashed: true, // custom attribute - metadata only
            attrs: {
                line: {
                    stroke: '#EB8100',
                    strokeWidth: 3,
                    strokeDasharray: '6, 5',
                    targetMarker: {
                        d: 'M 10 -5 0 0 10 5 Z',
                    }
                },
            },
        }, joint.shapes.standard.Link.prototype.defaults);
    }
}

class OwnershipLink extends joint.shapes.standard.Link {
    defaults() {
        return joint.util.defaultsDeep({
            type: 'OwnershipLink',
            color: '#000000', // custom attribute - metadata only
            thin: true, // custom attribute - metadata only
            attrs: {
                line: {
                    stroke: '#000000',
                    strokeWidth: 1,
                    targetMarker: null
                },
            },
        }, joint.shapes.standard.Link.prototype.defaults);
    }
}

Object.assign(joint.shapes, { PhysicalLink, LegalLink, PaymentLink, ServiceLink, IPTransferLink, IPLicenseLink, AgreementLink, MiscellaneousLink, OwnershipLink });

export { MiscellaneousLink };

export function createFlowLink(flowType: FlowType): joint.shapes.standard.Link {
    switch (flowType) {
        case FlowType.Physical:
            return new PhysicalLink();
        case FlowType.Legal:
            return new LegalLink();
        case FlowType.Payment:
            return new PaymentLink();
        case FlowType.Service:
            return new ServiceLink();
        case FlowType.IPTransfer:
            return new IPTransferLink();
        case FlowType.IPLicense:
            return new IPLicenseLink();
        case FlowType.Agreement:
            return new AgreementLink();
        case FlowType.Ownership:
            return new OwnershipLink();
        case FlowType.Miscellaneous:
            return new MiscellaneousLink();
        default:
            return new PaymentLink();
    }
}

// Shared text-label spec for flow links. Mirrors the markup used by the link
// context-menu "Add Label" action so programmatically-added labels look identical.
export function createFlowLinkTextLabel(text: string, position: number = 0.5): joint.dia.Link.Label {
    return {
        position,
        attrs: {
            rect: {
                ref: 'text',
                width: 'calc(w+12)',
                height: 'calc(h+10)',
                x: 'calc(x-6)',
                y: 'calc(y-5)',
                stroke: '#D0D0CE',
                cursor: 'pointer',
                fill: '#FFFFFF',
            },
            text: {
                textAnchor: 'middle',
                textVerticalAnchor: 'middle',
                fill: '#000000',
                fontSize: 14,
                fontFamily: '"Open Sans", sans-serif',
                fontWeight: 'bold',
                text,
            },
        },
    };
}
